<script setup lang="ts">
import { useId } from 'vue'

withDefaults(defineProps<{
  label: string
  modelValue: string
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'date'
  autocomplete?: string
  placeholder?: string
  inputmode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'
  maxlength?: number
  error?: string
  hint?: string
  required?: boolean
  disabled?: boolean
}>(), {
  type: 'text',
  autocomplete: undefined,
  placeholder: undefined,
  inputmode: undefined,
  maxlength: undefined,
  error: undefined,
  hint: undefined,
  required: false,
  disabled: false
})
defineEmits<{ 'update:modelValue': [value: string] }>()

const fieldId = useId()
const errorId = `${fieldId}-error`
const hintId = `${fieldId}-hint`
</script>

<template>
  <div class="ck-field">
    <label :for="fieldId" class="ck-field-label">{{ label }}<span v-if="required" aria-hidden="true"> *</span></label>
    <input
      :id="fieldId"
      :type="type"
      :value="modelValue"
      :autocomplete="autocomplete"
      :placeholder="placeholder"
      :inputmode="inputmode"
      :maxlength="maxlength"
      :required="required"
      :disabled="disabled"
      :aria-invalid="!!error"
      :aria-describedby="error ? errorId : hint ? hintId : undefined"
      @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <span v-if="error" :id="errorId" class="ck-field-error">{{ error }}</span>
    <span v-else-if="hint" :id="hintId" class="ck-field-hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.ck-field { display: block; margin-bottom: var(--space-4); text-align: left; }
.ck-field-label { display: block; margin-bottom: var(--space-1); font-size: var(--fs-sm); font-weight: var(--fw-semibold); color: var(--color-text-muted); }
input {
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--ck-cream-300);
  border-radius: var(--radius-md);
  background: var(--ck-cream-50);
  color: var(--ck-text);
  font: inherit;
  transition: border-color 150ms ease-out, box-shadow 150ms ease-out;
}
input:focus-visible { outline: none; border-color: var(--ck-orange-500); box-shadow: 0 0 0 3px rgba(237, 107, 26, .22); }
input[aria-invalid="true"] { border-color: var(--ck-fail-500); }
input:disabled { cursor: not-allowed; opacity: .6; }
.ck-field-error { display: block; margin-top: var(--space-1); font-size: var(--fs-xs); color: var(--ck-fail-500); }
.ck-field-hint { display: block; margin-top: var(--space-1); font-size: var(--fs-xs); color: var(--color-text-faint); }

@media (prefers-reduced-motion: reduce) {
  input { transition: none; }
}
</style>
